/** `cache`: last server data per user. `queue`: signatures waiting to reach the server. */
export type OfflineBucket = 'cache' | 'queue';

const BUCKETS: readonly OfflineBucket[] = ['cache', 'queue'];

/** What an `update` does with the value it just read. */
export type Mutation<T> = { op: 'put'; value: T } | { op: 'remove' } | { op: 'keep' };

/** The only thing the offline features need from IndexedDB, so tests can swap in memory. */
export interface OfflineStore {
  get<T>(bucket: OfflineBucket, key: string): Promise<T | undefined>;
  put(bucket: OfflineBucket, key: string, value: unknown): Promise<void>;
  remove(bucket: OfflineBucket, key: string): Promise<void>;
  /**
   * Atomic read-modify-write of one key: `decide` sees the stored value and
   * says what to do with it, all inside one transaction, so two tabs can never
   * lose an update or resurrect a removed item. `decide` must be synchronous.
   * Resolves with the value that was read.
   */
  update<T>(bucket: OfflineBucket, key: string, decide: (current: T | undefined) => Mutation<T>): Promise<T | undefined>;
  entries<T>(bucket: OfflineBucket): Promise<Array<[string, T]>>;
}

export function createMemoryStore(): OfflineStore {
  const data = new Map<string, Map<string, unknown>>();
  const bucketOf = (bucket: OfflineBucket): Map<string, unknown> => {
    let found = data.get(bucket);

    if (found === undefined) {
      found = new Map();
      data.set(bucket, found);
    }

    return found;
  };

  return {
    get: <T>(bucket: OfflineBucket, key: string) =>
      Promise.resolve(bucketOf(bucket).get(key) as T | undefined),
    put: (bucket, key, value) => {
      bucketOf(bucket).set(key, structuredClone(value));

      return Promise.resolve();
    },
    remove: (bucket, key) => {
      bucketOf(bucket).delete(key);

      return Promise.resolve();
    },
    update: <T>(bucket: OfflineBucket, key: string, decide: (current: T | undefined) => Mutation<T>) => {
      const found = bucketOf(bucket);
      const current = found.get(key) as T | undefined;
      const mutation = decide(current);

      if (mutation.op === 'put') {
        found.set(key, structuredClone(mutation.value));
      } else if (mutation.op === 'remove') {
        found.delete(key);
      }

      return Promise.resolve(current);
    },
    entries: <T>(bucket: OfflineBucket) =>
      Promise.resolve([...bucketOf(bucket).entries()] as Array<[string, T]>),
  };
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

/** Resolves with `read()` once the transaction committed, not when its last request queued. */
function committed<T>(transaction: IDBTransaction, read: () => T): Promise<T> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve(read());
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'));
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
  });
}

/** Native IndexedDB, no dependency: one object store per bucket, out-of-line keys. */
export function createIndexedDbStore(name = 'punta-norte-offline'): OfflineStore {
  let opened: Promise<IDBDatabase> | null = null;

  const open = (): Promise<IDBDatabase> => {
    if (opened !== null) {
      return opened;
    }

    const attempt = new Promise<IDBDatabase>((resolve, reject) => {
      const fail = (error: unknown): void => {
        reject(error instanceof Error ? error : new Error('IndexedDB unavailable'));
      };

      try {
        const request = indexedDB.open(name, 1);

        request.onupgradeneeded = () => {
          for (const bucket of BUCKETS) {
            request.result.createObjectStore(bucket);
          }
        };
        request.onsuccess = () => {
          const db = request.result;

          // Another tab upgrades, or the browser drops the connection: forget this handle so the next call reopens.
          db.onversionchange = () => {
            db.close();
            opened = null;
          };
          db.onclose = () => {
            opened = null;
          };
          resolve(db);
        };
        request.onerror = () => fail(request.error);
        request.onblocked = () => fail(new Error('IndexedDB blocked by another tab'));
      } catch (error) {
        fail(error);
      }
    });

    opened = attempt;
    // Never keep a failed open: the next operation must try again.
    attempt.catch(() => {
      if (opened === attempt) {
        opened = null;
      }
    });

    return attempt;
  };

  return {
    get: async <T>(bucket: OfflineBucket, key: string) => {
      const db = await open();

      return requestResult(db.transaction(bucket, 'readonly').objectStore(bucket).get(key) as IDBRequest<T | undefined>);
    },
    put: async (bucket, key, value) => {
      const db = await open();
      const transaction = db.transaction(bucket, 'readwrite');

      transaction.objectStore(bucket).put(value, key);
      await committed(transaction, () => undefined);
    },
    remove: async (bucket, key) => {
      const db = await open();
      const transaction = db.transaction(bucket, 'readwrite');

      transaction.objectStore(bucket).delete(key);
      await committed(transaction, () => undefined);
    },
    update: async <T>(bucket: OfflineBucket, key: string, decide: (current: T | undefined) => Mutation<T>) => {
      const db = await open();
      const transaction = db.transaction(bucket, 'readwrite');
      const objectStore = transaction.objectStore(bucket);
      const read = objectStore.get(key) as IDBRequest<T | undefined>;
      const done = committed(transaction, () => read.result);

      read.onsuccess = () => {
        try {
          const mutation = decide(read.result);

          if (mutation.op === 'put') {
            objectStore.put(mutation.value, key);
          } else if (mutation.op === 'remove') {
            objectStore.delete(key);
          }
        } catch (error) {
          // A throwing `decide` must not leave half a decision behind.
          transaction.abort();
          throw error;
        }
      };

      return done;
    },
    entries: async <T>(bucket: OfflineBucket) => {
      const db = await open();
      const transaction = db.transaction(bucket, 'readonly');
      const objectStore = transaction.objectStore(bucket);
      const keys = objectStore.getAllKeys();
      const values = objectStore.getAll() as IDBRequest<T[]>;

      // Keys and values come from the same snapshot, so they always line up.
      return committed(transaction, () =>
        keys.result.map((key, index): [string, T] => [key as string, values.result[index] as T]),
      );
    },
  };
}

let shared: OfflineStore | null = null;

/** IndexedDB in the browser; memory where it does not exist (jsdom, very old browsers), so the app still works online. */
export function getOfflineStore(): OfflineStore {
  shared ??= typeof indexedDB === 'undefined' ? createMemoryStore() : createIndexedDbStore();

  return shared;
}
